import * as H from 'quickstart/hacks'
import type {AdminContext, AdminContextFromServer, Hack} from 'quickstart/types'
import * as React from 'react'
import {logger} from 'tizra/log'

const log = logger('useAdminContext')

export const defaultAdminContextFromServer: AdminContextFromServer = {
  debugFlags: '',
  globalConfig: {},
  hacks: [],
  metaType: 'SiteDisplay',
  metaTypes: [],
  reskin: false,
  siteDisplayId: 0,
}

export const fromServer = (
  context: AdminContextFromServer,
  global: boolean,
): AdminContext => ({
  ...defaultAdminContextFromServer,
  ...context,
  hacks: H.fromServer(context.hacks),
  global,
})

export const defaultAdminContext: AdminContext = fromServer(
  defaultAdminContextFromServer,
  false,
)

const AC = React.createContext<AdminContext>(defaultAdminContext)

export const AdminContextProvider = ({
  context,
  global,
  children,
}: {
  context: AdminContextFromServer
  global: boolean
  children: React.ReactNode
}) => {
  const value = React.useMemo(
    () => fromServer(context, global),
    [context, global],
  )
  return <AC.Provider value={value}>{children}</AC.Provider>
}

export const useAdminContext = () => React.useContext(AC)

/**
 * Which admin this form is rendering into: `true` for the reskinned admin,
 * `false` for the classic one.
 *
 * This is the branching helper every `A.*` component uses to choose its
 * rendering (design.md — Decision 1: branch per component, not per namespace).
 * The signal is supplied by the admin itself and threaded through
 * `AdminContextFromServer`; it is never derived by inspecting the document.
 *
 * A release of the admin that predates the reskin switch simply omits the
 * field, so the classic branch is what an absent signal selects.
 */
export const useReskin = () => useAdminContext().reskin

export const useHack = (s: Hack) => !!useAdminContext().hacks[s]
